import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
    viewChild
} from "@angular/core";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { AsyncPipe, NgClass } from "@angular/common";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatOptionSelectionChange } from "@angular/material/core";
import { MatSelect, MatSelectChange, MatSelectModule } from "@angular/material/select";
import {
    ButtonComponent,
    ButtonVariant,
    CheckboxComponent,
    InputComponent,
    LabelPosition,
    PaginatorComponent,
    TextareaComponent,
    ToggleComponent,
    TooltipDirective
} from "@sxm-content-creators/tools-ds";
import {
    catchError,
    combineLatest,
    concat,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Observable,
    of,
    skip,
    Subject,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs";
import { AuthService } from "../../../../../auth/auth.service";
import { NavbarService } from "../../../../../layouts/tenant-interactive-layout/navbar.service";
import { BadgeResponse, SegmentConfigResponse } from "../../../../../admin/themes/models/theme.model";
import {
    ConfirmModalComponent,
    ConfirmModalData
} from "../../../../../components/confirm-modal/confirm-modal.component";
import { SnackbarService } from "../../../../../components/snackbar/snackbar.service";
import { TagComponent, TagOption } from "../../../../../components/tag/tag.component";
import { TagListComponent } from "../../../../../components/tag/tag-list.component";
import { TzDatePipe } from "../../../../pipes/tz-date.pipe";
import { SplitterPipe } from "../../../../../shared/pipes/splitter.pipe";
import { TagCategoryKey } from "../../../../models/freeform-tags.model";
import { FORM_CONSTRAINTS } from "../../../../components/interactive-form/interactive-form.validators";
import { FreeformApiService } from "../../../../services/freeform.api.service";
import { SubmissionFreeformApiService } from "../../../services/submission-freeform.api.service";
import { SubmitterApiService } from "../../../services/submitter.api.service";
import { SubmitterContactResponse } from "../../../models/submitter.model";
import {
    DisplaySettingsModalComponent,
    DisplaySettingsModalData
} from "../../../components/display-settings-modal/display-settings-modal.component";
import {
    MoveSubmissionModalComponent,
    MoveSubmissionModalData
} from "../../../components/move-submission-modal/move-submission-modal.component";
import {
    ResponseModalComponent,
    ModalData,
    ResponseModalResult
} from "../../../components/response-modal/response-modal.component";
import {
    AUDIO_FILE_EXTENSIONS,
    FreeformSortColumn,
    isSubmissionAudioMimeType,
    isSubmissionVideoMimeType,
    MAX_AUDIO_SIZE_BYTES,
    MAX_AUDIO_SIZE_MB,
    SubmissionFreeformResponse,
    SubmissionFreeformUpdateCleanedTextAndTagsRequest,
    VIDEO_FILE_EXTENSIONS
} from "../../../models/submission-freeform.model";
import {
    COLUMN_OPTIONS,
    FILTER_BY_OPTIONS,
    FilterByValue,
    FreeformFilterService,
    MIN_SCORE_OPTIONS,
    MinScoreValue,
    PUBLIC_DISPLAY_OPTIONS,
    VISIBILITY_OPTIONS
} from "./freeform-filter.service";
import { FreeformDashboardDataSource } from "./freeform-dashboard.datasource";
import { PageEvent } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatIconButton } from "@angular/material/button";
import { HttpErrorResponse } from "@angular/common/http";

const SHOW_TAGS_STORAGE_KEY = "freeformDashboard.showTags";
const MAX_VISIBLE_SUBMISSION_TAGS = 4;
const MAX_QUEUED = 20;
const VOICE_SUBMISSION_MAX_TEXT_LENGTH = 1000;
const TEXT_SUBMISSION_MAX_TEXT_LENGTH = FORM_CONSTRAINTS.maxCharacterOverride.max;
const NOTES_MAX_LENGTH = 100;
const MAX_DOWNLOAD_FILENAME_TAGS = 3;
const MAX_DOWNLOAD_FILENAME_LENGTH = 200; // Filesystems generally cap a filename at 255 chars; this leaves room for the extension.

type SubmissionTab = "submission" | "notes" | "response";
const SUBMISSION_TABS: SubmissionTab[] = ["submission", "notes", "response"];

const TAG_CATEGORY_KEYS: readonly TagCategoryKey[] = [
    "tagCategoryA",
    "tagCategoryB",
    "tagCategoryC",
    "tagCategoryD",
    "tagCategoryE",
    "tagCategoryF"
];

@Component({
    selector: "app-freeform-dashboard",
    templateUrl: "./freeform-dashboard.component.html",
    styleUrl: "./freeform-dashboard.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AsyncPipe,
        NgClass,
        CheckboxComponent,
        TzDatePipe,
        SplitterPipe,
        InputComponent,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        FormsModule,
        TagComponent,
        TagListComponent,
        ToggleComponent,
        ButtonComponent,
        TextareaComponent,
        MatTableModule,
        MatSortModule,
        PaginatorComponent,
        MatProgressBar,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatIconButton,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        TooltipDirective
    ],
    providers: [FreeformFilterService]
})
export class FreeformDashboardComponent implements OnInit, OnDestroy {
    private readonly freeformApi = inject(FreeformApiService);
    private readonly submissionFreeformApi = inject(SubmissionFreeformApiService);
    private readonly submitterApi = inject(SubmitterApiService);
    private readonly snackbar = inject(SnackbarService);
    protected readonly filters = inject(FreeformFilterService);
    private readonly navbarService = inject(NavbarService);
    private readonly dialog = inject(MatDialog);
    private readonly auth = inject(AuthService);

    // Marketers have read-only access to this dashboard
    protected readonly isMarketer = computed(() => this.auth.isMarketer());

    private readonly interactiveIds$ = this.navbarService.selectedInteractiveIds$.pipe(filter(ids => ids.length > 0));
    private readonly filterChange$ = toObservable(this.filters.searchFilter).pipe(skip(1));
    private readonly reloadIds$ = merge(this.interactiveIds$, this.interactiveIdsOn(this.navbarService.refresh$));
    private readonly tenantChange$ = toObservable(this.navbarService.tenantId).pipe(skip(1));
    // Only fires when a submission mutation actually changes its tags (see {@link updateSubmission}'s `refetchTags` param)
    private readonly tagsRefetchRequested$ = new Subject<void>();
    // Fires after a segment change or delete, so the Segment filter's counts stay current
    private readonly segmentsRefetchRequested$ = new Subject<void>();

    readonly searchControl = new FormControl("");

    readonly showTags = signal(this.readShowTagsFromStorage());
    readonly categoryList = toSignal(
        merge(
            this.reloadIds$.pipe(map(ids => ({ ids, refreshTableAfter: false }))),
            // A (table) page of submissions should be refreshed *after* tag are refetched and pruned.
            this.interactiveIdsOn(this.tagsRefetchRequested$).pipe(map(ids => ({ ids, refreshTableAfter: true })))
        ).pipe(
            switchMap(({ ids, refreshTableAfter }) =>
                this.freeformApi.getTags(ids).pipe(
                    map(response =>
                        TAG_CATEGORY_KEYS.flatMap(key => {
                            const category = response[key];
                            return category ? [{ ...category, key }] : [];
                        })
                    ),
                    tap(categories => {
                        this.setAvailableColumns(this.segmentOptions(), categories);

                        // Prune stale values: drop any selected tags the refreshed list no longer has
                        const validTagsByCategory = new Map<TagCategoryKey, Set<string>>(
                            categories.map(c => [c.key, new Set(c.tags.map(t => t.tag))])
                        );
                        const currentTags = this.filters.tags();
                        const prunedTags = currentTags.filter(({ categoryKey, tag }) =>
                            validTagsByCategory.get(categoryKey)?.has(tag)
                        );
                        // Skip setTags when nothing was actually pruned. Signals compare by reference, writing a new-but-equivalent array
                        // would mark the filters as changed and trigger an additional/unnecessary page refetch
                        if (prunedTags.length !== currentTags.length) this.filters.setTags(prunedTags);

                        if (refreshTableAfter) this.dataSource.refreshPage();
                    }),
                    catchError(() => {
                        this.snackbar.show("We couldn't load the AI Prompt Filters. Try again.", "error");
                        if (refreshTableAfter) this.dataSource.refreshPage();
                        return of([]);
                    })
                )
            )
        ),
        { initialValue: null }
    );

    readonly segmentOptions = toSignal<SegmentConfigResponse[], SegmentConfigResponse[]>(
        merge(this.reloadIds$, this.interactiveIdsOn(this.segmentsRefetchRequested$)).pipe(
            switchMap(ids =>
                this.freeformApi.getSegments(ids).pipe(
                    map(response => response.segments),
                    tap(segments => {
                        this.setAvailableColumns(segments, this.categoryList() ?? []);

                        // Prune a stale selection: drop it if the refreshed list no longer has it.
                        const currentSegmentId = this.filters.segmentId();
                        if (currentSegmentId !== null && !segments.some(s => s.id === currentSegmentId)) {
                            this.filters.setSegmentId(null);
                        }
                    }),
                    catchError(() => {
                        this.snackbar.show("We couldn't load the Segment filter options.", "error");
                        return of([]);
                    })
                )
            )
        ),
        { initialValue: [] }
    );

    /** Segment is only offered when segments are configured; a column is added per tag category.
     *  Called from both async loads, so each passes its own fresh result and reads the other's current one. */
    private setAvailableColumns(segments: SegmentConfigResponse[], categories: { key: string }[]): void {
        this.filters.setAvailableColumns([
            ...COLUMN_OPTIONS.filter(col => col !== "Source" && (col !== "Segment" || segments.length > 0)),
            ...categories.map(c => c.key),
            "Source"
        ]);
    }

    readonly tableColumns = computed(() => [
        ...(this.isMarketer() ? [] : ["select"]),
        ...this.filters.selectedColumns()
    ]);

    readonly columnLabel = computed(() => {
        const categories = this.categoryList() ?? [];
        return (col: string) => {
            const label = categories.find(c => c.key === col)?.label;
            if (label) return label;
            const categoryIndex = TAG_CATEGORY_KEYS.indexOf(col as TagCategoryKey);
            return categoryIndex === -1 ? col : `Category${categoryIndex + 1}`;
        };
    });

    readonly segmentLabel = computed(() => {
        const segments = this.segmentOptions();
        return (segmentId: string) => segments.find(s => s.id === segmentId)?.label ?? segmentId;
    });

    segmentFilterLabel(segment: SegmentConfigResponse): string {
        return segment.count > 0 ? `${segment.label} (${segment.count})` : segment.label;
    }

    /** Builds the disabled, prepended option for a submission's segment that no longer exists in the configured list. */
    unconfiguredSegmentOption(submission: SubmissionFreeformResponse): SegmentConfigResponse {
        return {
            id: submission.segmentId ?? "",
            label: submission.segmentLabel ?? submission.segmentId ?? "",
            description: null,
            displayOrder: 0,
            count: 0
        };
    }

    isUnconfiguredSegmentOption(submission: SubmissionFreeformResponse): boolean {
        return !!submission.segmentId && !this.segmentOptions().some(s => s.id === submission.segmentId);
    }

    readonly activeFilterLabels = computed(() => this.filters.filterLabels(this.columnLabel(), this.segmentLabel()));

    readonly interactiveName = computed(() => {
        const namesById = new Map(
            this.navbarService.allInteractives().map(interactive => [interactive.id, interactive.name])
        );
        return (interactiveId: string) => namesById.get(interactiveId) ?? interactiveId;
    });

    private readonly tableContainer = viewChild.required<ElementRef>("tableContainer");
    private readonly submissionTextEl = viewChild<ElementRef<HTMLElement>>("submissionText");
    private readonly replaceAudioFileInput = viewChild.required<ElementRef<HTMLInputElement>>("replaceAudioFileInput");

    readonly checkedIds = signal<Set<string>>(new Set());
    readonly activeSubmission = signal<SubmissionFreeformResponse | null>(null);
    readonly activeTab = signal<SubmissionTab>("submission");

    private readonly audioUrlCache = new Map<string, string>();
    private readonly audioRefreshRequested$ = new Subject<string>();
    readonly activeAudioUrl = toSignal(
        merge(
            toObservable(this.activeSubmission).pipe(
                map(submission => (submission?.hasAudio ? submission.id : null)),
                distinctUntilChanged()
            ),
            this.audioRefreshRequested$
        ).pipe(
            switchMap(id => {
                if (!id) return of(null);

                const cachedUrl = this.audioUrlCache.get(id);
                if (cachedUrl) return of(cachedUrl);

                // Emit null first so the player clears immediately on switch, instead of showing
                // the previous submission's stale audio while this one's fetch is in flight.
                return concat(
                    of(null),
                    this.submissionFreeformApi.getAudioFile(id).pipe(
                        map(audioFile => audioFile.url),
                        tap(url => this.audioUrlCache.set(id, url)),
                        catchError(() => of(null))
                    )
                );
            })
        ),
        { initialValue: null }
    );

    private readonly responseAudioUrlCache = new Map<string, string>();
    // Response audio is lazily fetched, like badges, only needed when the audio player is visible in the response tab.
    readonly activeResponseAudioUrl = toSignal(
        combineLatest([toObservable(this.activeTab), toObservable(this.activeSubmission)]).pipe(
            map(([tab, submission]) => (tab === "response" && submission?.responseAudioPath ? submission.id : null)),
            switchMap(id => {
                if (!id) return of(null);

                const cachedUrl = this.responseAudioUrlCache.get(id);
                if (cachedUrl) return of(cachedUrl);

                // Emit null first so the player clears immediately on switch, instead of showing
                // the previous submission's stale response audio while this one's fetch is in flight.
                return concat(
                    of(null),
                    this.submissionFreeformApi.getResponseAudioFile(id).pipe(
                        map(audioFile => audioFile.url),
                        tap(url => this.responseAudioUrlCache.set(id, url)),
                        catchError(() => of(null))
                    )
                );
            })
        ),
        { initialValue: null }
    );

    readonly queueControl = new FormControl<number | null>(null);
    readonly isTranscriptExpanded = signal(false);
    readonly isTranscriptClamped = computed(() => {
        const el = this.submissionTextEl()?.nativeElement;
        // re-compute clamped state when active submission changes (instead of resize event) or when expanded state changes
        this.activeSubmission();
        this.isTranscriptExpanded();
        return !!el && el.scrollHeight > el.clientHeight;
    });

    readonly isInEditMode = signal<boolean>(false);
    readonly editCleanedTextMaxLength = computed(() =>
        this.activeSubmission()?.hasAudio ? VOICE_SUBMISSION_MAX_TEXT_LENGTH : TEXT_SUBMISSION_MAX_TEXT_LENGTH
    );
    readonly editCleanedTextControl = new FormControl<string>("", control =>
        Validators.maxLength(this.editCleanedTextMaxLength())(control)
    );
    readonly removedEditTagKeys = signal<Set<TagCategoryKey>>(new Set());
    readonly editTagOptions = computed<TagOption<TagCategoryKey>[]>(() => {
        const submission = this.activeSubmission();
        if (!submission) return [];

        return TAG_CATEGORY_KEYS.flatMap(key => {
            const label = submission[key];
            return label && !this.removedEditTagKeys().has(key) ? [{ value: key, label }] : [];
        });
    });
    readonly editNotesControl = new FormControl<string>("", [
        Validators.required,
        Validators.maxLength(NOTES_MAX_LENGTH)
    ]);

    // null = theme not loaded (or its fetch failed); an empty array is a loaded theme with no badges
    readonly badges = computed<BadgeResponse[] | null>(() => {
        const theme = this.navbarService.selectedTheme();
        return theme ? (theme.freeformConfig?.badgesConfig ?? []) : null;
    });
    readonly audioEnergyLabel = computed(
        () => this.navbarService.selectedTheme()?.freeformConfig?.aiConfig?.audioEnergyLabel?.trim() || null
    );

    // Accumulates every submitter resolved so far, keyed by submitterContactId, so re-encountering the same id never triggers a refetch.
    readonly submittersCache = signal<Map<string, SubmitterContactResponse>>(new Map());

    readonly dataSource = new FreeformDashboardDataSource(
        this.submissionFreeformApi,
        page => {
            const sort = page.sort;
            const sortColumnMap: Record<string, FreeformSortColumn> = {
                Score: "SCORE",
                Queue: "PRIORITY",
                Date: "SUBMISSION_TIME"
            };

            return {
                interactiveIds: this.navbarService.selectedInteractiveIds(),
                page: page.index,
                size: page.size,
                sortColumn: sortColumnMap[sort.active] ?? "SUBMISSION_TIME",
                sortDirection: sort.direction === "asc" ? "ASC" : "DESC",
                ...this.filters.searchFilter()
            };
        },
        this.filterChange$
    );

    // The currently selected interactive ids, re-emitted whenever `trigger$` fires
    private interactiveIdsOn(trigger$: Observable<void>): Observable<string[]> {
        return trigger$.pipe(
            withLatestFrom(this.interactiveIds$),
            map(([, ids]) => ids)
        );
    }

    ngOnInit(): void {
        this.searchControl.valueChanges.pipe(debounceTime(200)).subscribe(value => {
            this.filters.setSearchQuery(value ?? "");
        });

        // The component is constructed before NavigationEnd, so selectedInteractiveIds still holds
        // the previous route's ID at field init time. By ngOnInit, NavigationEnd has fired and
        // the datasource's skip(1) has been consumed, so this triggers the correct first load.
        this.dataSource.refreshPage();
        this.reloadIds$.pipe(skip(1)).subscribe(() => this.dataSource.refreshPage());
        // Badges are tenant-scoped, so don't leave the response tab showing the previous tenant's
        this.tenantChange$.subscribe(() => this.setActiveTab("submission"));
        this.dataSource.page.pipe(skip(1)).subscribe(() => this.checkedIds.set(new Set()));
        this.dataSource
            .connect()
            .pipe(skip(1))
            .subscribe(data => {
                const activeId = this.activeSubmission()?.id;
                // preserve the active row's up-to-date record if it's still in the new page
                const stillActive = activeId ? data.find(row => row.id === activeId) : undefined;
                const nextActive = stillActive ?? data[0] ?? null;
                this.clearAudioUrlCache(nextActive?.id);
                this.setActiveSubmission(nextActive);
                this.loadSubmitters(data);
            });
    }

    ngOnDestroy(): void {
        this.clearAudioUrlCache();
        this.clearResponseAudioUrlCache();
    }

    onTableClick(event: Event): void {
        const target = event.target as HTMLElement;
        const rowEl = target.closest<HTMLElement>("tr[data-row-id]");
        if (!rowEl) return;

        const row = this.dataSource.data.find(r => r.id === rowEl.dataset["rowId"]);
        if (!row) return;

        if (target.closest("td.mat-column-select")) {
            this.toggleCheck(row);
        } else {
            this.setActiveSubmission(row);
        }
    }

    private setActiveSubmission(submission: SubmissionFreeformResponse | null): void {
        this.activeSubmission.set(submission);
        this.queueControl.setValue(submission?.priority ?? null, { emitEvent: false });
        this.isInEditMode.set(false);
        this.isTranscriptExpanded.set(false);
    }

    setActiveTab(tab: SubmissionTab): void {
        this.activeTab.set(tab);
        this.isInEditMode.set(false);
    }

    private loadSubmitters(data: SubmissionFreeformResponse[]): void {
        const ids = Array.from(
            new Set(data.map(submission => submission.submitterContactId).filter((id): id is string => !!id))
        );
        const uncachedIds = ids.filter(id => !this.submittersCache().has(id));
        if (!uncachedIds.length) return;

        this.submitterApi
            .getSubmitters(uncachedIds)
            .pipe(
                map(response => response.items),
                catchError(() => {
                    this.snackbar.show("We couldn't load submitter details.", "error");
                    return of<SubmitterContactResponse[]>([]);
                })
            )
            .subscribe(items => {
                if (!items.length) return;
                this.submittersCache.update(current => {
                    const next = new Map(current);
                    items.forEach(item => next.set(item.submitterContactId, item));
                    return next;
                });
            });
    }

    submitterFor(submission: SubmissionFreeformResponse): SubmitterContactResponse | null {
        const id = submission.submitterContactId;
        return id ? (this.submittersCache().get(id) ?? null) : null;
    }

    submitterLocation(submitter: SubmitterContactResponse): string | null {
        const cityState = [submitter.city, submitter.state].filter((value): value is string => !!value).join(", ");
        return cityState || submitter.countryCode || null;
    }

    toggleTranscript(): void {
        this.isTranscriptExpanded.update(expanded => !expanded);
    }

    isAllSelected(): boolean {
        const selected = this.checkedIds();
        const data = this.dataSource.data;
        return data.length > 0 && data.every(row => selected.has(row.id));
    }

    isRowSelected(row: SubmissionFreeformResponse): boolean {
        return this.checkedIds().has(row.id);
    }

    toggleCheck(row: SubmissionFreeformResponse): void {
        this.checkedIds.update(ids => {
            const next = new Set(ids);
            if (next.has(row.id)) {
                next.delete(row.id);
            } else {
                next.add(row.id);
            }
            return next;
        });
    }

    toggleAllChecks(): void {
        if (this.isAllSelected()) {
            this.clearChecked();
        } else {
            this.checkedIds.set(new Set(this.dataSource.data.map(r => r.id)));
        }
    }

    clearChecked(): void {
        this.checkedIds.set(new Set());
    }

    onMinScoreOptionSelect(event: MatOptionSelectionChange<MinScoreValue>): void {
        if (event.isUserInput && event.source.value === this.filters.minScore()) {
            this.filters.setMinScore(null);
        }
    }

    filterByLabel(value: FilterByValue): string {
        return FILTER_BY_OPTIONS.find(o => o.value === value)?.label ?? value;
    }

    onSegmentOptionSelect(event: MatOptionSelectionChange<string>): void {
        if (event.isUserInput && event.source.value === this.filters.segmentId()) {
            this.filters.setSegmentId(null);
        }
    }

    onBulkAction(
        value: "move" | "make-public" | "delete" | "download-audio" | "download-video",
        select: MatSelect
    ): void {
        const selectedSubmissions = this.dataSource.data.filter(row => this.checkedIds().has(row.id));
        if (value === "move") {
            this.openMoveSubmissionModal(selectedSubmissions);
        } else if (value === "make-public") {
            this.makeSubmissionsPublic(selectedSubmissions);
        } else if (value === "delete") {
            this.openDeleteConfirmModal(selectedSubmissions);
        } else if (value === "download-audio") {
            this.downloadSubmissionsAudio(selectedSubmissions);
        } else if (value === "download-video") {
            this.downloadSubmissionsVideo(selectedSubmissions);
        }
        select.writeValue(null);
    }

    onMoveSubmissionClick(submission: SubmissionFreeformResponse): void {
        this.openMoveSubmissionModal([submission]);
    }

    onViewAllFromListenerClick(submission: SubmissionFreeformResponse): void {
        const { profileId, deviceId } = submission;
        if (!profileId && !deviceId) return;
        this.filters.setSubmittedBy(profileId, deviceId);
    }

    onStopViewingAllFromListenerClick(): void {
        this.filters.setSubmittedBy(null, null);
    }

    onDeleteSubmissionClick(submission: SubmissionFreeformResponse): void {
        this.openDeleteConfirmModal([submission]);
    }

    onDownloadAudioClick(submission: SubmissionFreeformResponse): void {
        this.downloadSubmissionsAudio([submission]);
    }

    onDownloadVideoClick(submission: SubmissionFreeformResponse): void {
        this.downloadSubmissionsVideo([submission]);
    }

    private openMoveSubmissionModal(submissions: SubmissionFreeformResponse[]): void {
        const excludedIds = new Set(submissions.map(submission => submission.interactiveId));

        // Valid move targets: not a poll, not one of the submissions' current interactive(s).
        const interactiveOptions = this.navbarService
            .allInteractives()
            .filter(interactive => !interactive.isPoll && !excludedIds.has(interactive.id));

        this.dialog
            .open<MoveSubmissionModalComponent, MoveSubmissionModalData, boolean>(MoveSubmissionModalComponent, {
                data: { submissionIds: submissions.map(submission => submission.id), interactiveOptions }
            })
            .afterClosed()
            .subscribe(shouldRefresh => {
                if (shouldRefresh) this.tagsRefetchRequested$.next();
            });
    }

    private openDeleteConfirmModal(submissions: SubmissionFreeformResponse[]): void {
        const isSingle = submissions.length === 1;

        this.dialog
            .open<ConfirmModalComponent, ConfirmModalData, boolean>(ConfirmModalComponent, {
                data: {
                    title: `Delete Submission${isSingle ? "" : "s"}?`,
                    message: "This action is permanent and can't be undone.",
                    confirmLabel: `Delete Submission${isSingle ? "" : "s"}`,
                    danger: true
                }
            })
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) this.deleteSubmissions(submissions);
            });
    }

    private deleteSubmissions(submissions: SubmissionFreeformResponse[]): void {
        const isSingle = submissions.length === 1;
        const fallbackErrorMessage = `We couldn't delete the submission${isSingle ? "" : "s"}.`;

        this.submissionFreeformApi
            .deleteSubmissions(submissions.map(submission => submission.id))
            .pipe(
                tap(() => {
                    this.snackbar.show(`Submission${isSingle ? "" : "s"} Deleted.`, "success");
                    this.tagsRefetchRequested$.next();
                    this.segmentsRefetchRequested$.next();
                }),
                catchError((error: HttpErrorResponse) => {
                    this.snackbar.show(error.error?.detail ?? fallbackErrorMessage, "error");
                    return of(null);
                })
            )
            .subscribe();
    }

    private makeSubmissionsPublic(submissions: SubmissionFreeformResponse[]): void {
        const eligible = submissions.filter(submission => !this.isSpamOrArchived(submission));
        if (!eligible.length) {
            this.snackbar.show("Spam and archived submissions aren't eligible for public display.", "error");
            return;
        }

        const suffix = eligible.length === 1 ? "" : "s";
        const fallbackErrorMessage = `We couldn't make the submission${suffix} public.`;
        const skippedCount = submissions.length - eligible.length;
        const skippedSuffix = skippedCount === 1 ? "" : "s";
        const skippedMessage = skippedCount ? ` Skipped ${skippedCount} spam/archived submission${skippedSuffix}.` : "";

        this.submissionFreeformApi
            .bulkUpdateIsPublic(
                eligible.map(submission => submission.id),
                true
            )
            .pipe(
                tap(({ results }) => {
                    const failures = results.filter(result => result.status === "FAILURE");
                    if (failures.length === results.length) {
                        this.snackbar.show(fallbackErrorMessage, "error");
                        return;
                    }
                    // Per-id failures are "submission not found" (e.g. concurrently deleted), safe to
                    // disregard rather than fail the rest of the update.
                    if (failures.length) console.warn("Some submissions could not be made public:", failures);
                    this.snackbar.show(`Submission${suffix} Made Public.${skippedMessage}`, "success");
                    this.dataSource.refreshPage();
                }),
                catchError((error: HttpErrorResponse) => {
                    this.snackbar.show(error.error?.detail ?? fallbackErrorMessage, "error");
                    return of(null);
                })
            )
            .subscribe();
    }

    private downloadSubmissionsAudio(submissions: SubmissionFreeformResponse[]): void {
        const withAudio = submissions.filter(submission => submission.hasAudio);
        if (!withAudio.length) {
            this.snackbar.show("None of the selected submissions have audio to download.", "error");
            return;
        }

        withAudio.forEach(submission => {
            this.submissionFreeformApi
                .downloadAudioFile(submission.id)
                .pipe(
                    tap(blob => {
                        const url = URL.createObjectURL(blob);
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        anchor.download = `${this.downloadFilenameBase(submission)}${
                            isSubmissionAudioMimeType(blob.type) ? `.${AUDIO_FILE_EXTENSIONS[blob.type]}` : ""
                        }`;
                        anchor.click();
                        URL.revokeObjectURL(url);
                    }),
                    catchError(() => {
                        this.snackbar.show("We couldn't download this submission's audio.", "error");
                        return of(null);
                    })
                )
                .subscribe();
        });
    }

    private downloadSubmissionsVideo(submissions: SubmissionFreeformResponse[]): void {
        const withVideo = submissions.filter(submission => submission.hasVideo);
        if (!withVideo.length) {
            this.snackbar.show("None of the selected submissions have video to download.", "error");
            return;
        }

        withVideo.forEach(submission => {
            this.submissionFreeformApi
                .downloadVideoFile(submission.id)
                .pipe(
                    tap(blob => {
                        const url = URL.createObjectURL(blob);
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        anchor.download = `${this.downloadFilenameBase(submission)}${
                            isSubmissionVideoMimeType(blob.type) ? `.${VIDEO_FILE_EXTENSIONS[blob.type]}` : ""
                        }`;
                        anchor.click();
                        URL.revokeObjectURL(url);
                    }),
                    catchError(() => {
                        this.snackbar.show("We couldn't download this submission's video.", "error");
                        return of(null);
                    })
                )
                .subscribe();
        });
    }

    /**
     * Builds the base (extensionless) download filename from up to {@link MAX_DOWNLOAD_FILENAME_TAGS}
     * of the submission's tags, taken in A-to-F category order; a tag repeated among those is
     * collapsed to one `tag(count)` entry. Falls back to the submission id when it has no tags.
     */
    private downloadFilenameBase(submission: SubmissionFreeformResponse): string {
        const tags = this.submissionTags(submission).slice(0, MAX_DOWNLOAD_FILENAME_TAGS);
        if (!tags.length) return submission.id;

        const counts = new Map<string, number>();
        tags.forEach(tag => counts.set(tag, (counts.get(tag) ?? 0) + 1));

        const name = Array.from(counts, ([tag, count]) => (count > 1 ? `${tag}(${count})` : tag)).join("_");
        return name.slice(0, MAX_DOWNLOAD_FILENAME_LENGTH);
    }

    private clearAudioUrlCache(exceptId?: string): void {
        for (const [id, url] of this.audioUrlCache) {
            if (id === exceptId) continue;
            URL.revokeObjectURL(url);
            this.audioUrlCache.delete(id);
        }
    }

    private clearResponseAudioUrlCache(): void {
        for (const url of this.responseAudioUrlCache.values()) {
            URL.revokeObjectURL(url);
        }
        this.responseAudioUrlCache.clear();
    }

    onPageChange(event: PageEvent): void {
        this.dataSource.pageChange(event);
        this.tableContainer().nativeElement.scrollTo({ top: 0, behavior: "smooth" });
    }

    onPageSizeChange(pageSize: number): void {
        this.dataSource.pageChange({ pageIndex: 0, pageSize, length: this.dataSource.count$.value });
        // Defer scroll: page size selector is inside the scrollable container, so when it
        // closes it scrolls its focused overlay element back into view, undoing an immediate scrollTo(0).
        setTimeout(() => this.tableContainer().nativeElement.scrollTo({ top: 0, behavior: "smooth" }));
    }

    onShowTagsChange(checked: boolean): void {
        this.showTags.set(checked);
        localStorage.setItem(SHOW_TAGS_STORAGE_KEY, String(checked));
    }

    private readShowTagsFromStorage(): boolean {
        return localStorage.getItem(SHOW_TAGS_STORAGE_KEY) !== "false";
    }

    scoreTier(score: number | null): string | undefined {
        if (score === null) return undefined;
        if (score <= 49) return "bad";
        if (score <= 59) return "low";
        if (score <= 69) return "mid";
        if (score <= 79) return "good";
        if (score <= 89) return "high";
        return "elite";
    }

    isSpamOrArchived(submission: SubmissionFreeformResponse): boolean {
        return !!submission.isSpam || submission.state === "ARCHIVED";
    }

    onQueueChange(priority: number | null): void {
        const previousPriority = this.activeSubmission()?.priority ?? null;
        this.updateSubmission(
            submission =>
                priority
                    ? this.submissionFreeformApi.updatePriority(submission.id, priority)
                    : this.submissionFreeformApi.removePriority(submission.id),
            "We couldn't update the queue.",
            false,
            () => this.queueControl.setValue(previousPriority, { emitEvent: false })
        );
    }

    onMarkUsedChange(checked: boolean): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateIsUsed(submission.id, !checked),
            `An error has occurred when marking this submission as ${checked ? "unused" : "used"}.`
        );
    }

    onDisplaySettingsClick(): void {
        const submission = this.activeSubmission();
        if (!submission) return;

        this.dialog
            .open<DisplaySettingsModalComponent, DisplaySettingsModalData, boolean>(DisplaySettingsModalComponent, {
                data: { submission }
            })
            .afterClosed()
            .subscribe(updated => {
                if (updated) this.dataSource.refreshPage();
            });
    }

    onFavoriteClick(): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateIsFavorite(submission.id, !submission.isFavorite),
            "An error has occurred when toggling favoriting on this submission."
        );
    }

    onMarkAsNotSpamClick(): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateIsSpam(submission.id, false),
            "An error has occurred when marking this submission as not spam."
        );
    }

    onRestoreClick(): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateState(submission.id, "ACTIVE"),
            "An error has occurred when restoring this submission."
        );
    }

    onArchiveClick(): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateState(submission.id, "ARCHIVED"),
            "An error has occurred when archiving this submission."
        );
    }

    onReplaceAudioClick(): void {
        this.replaceAudioFileInput().nativeElement.click();
    }

    onReplaceAudioFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        const submissionId = this.activeSubmission()?.id;
        input.value = "";
        if (!file || !submissionId) return;

        if (!isSubmissionAudioMimeType(file.type)) {
            this.snackbar.show(`Unsupported file type: ${file.type}`, "error");
            return;
        }
        if (file.size > MAX_AUDIO_SIZE_BYTES) {
            this.snackbar.show(`File is too large. Max size is ${MAX_AUDIO_SIZE_MB} MB.`, "error");
            return;
        }

        this.submissionFreeformApi
            .replaceAudioFile(submissionId, file)
            .pipe(
                tap(() => {
                    this.snackbar.show("Audio replaced.", "success");
                    const cachedUrl = this.audioUrlCache.get(submissionId);
                    if (cachedUrl) URL.revokeObjectURL(cachedUrl);
                    this.audioUrlCache.delete(submissionId);
                    this.audioRefreshRequested$.next(submissionId);
                }),
                catchError((error: HttpErrorResponse) => {
                    this.snackbar.show(error.error?.detail ?? "We couldn't replace this submission's audio.", "error");
                    return of(null);
                })
            )
            .subscribe();
    }

    toggleEditMode(): void {
        // The response tab opens a modal rather than an inline edit form, so it must not flip
        // isInEditMode - that would hide the Edit button (guarded by !isInEditMode()) with no
        // inline "Cancel" to flip it back.
        if (this.activeTab() === "response") {
            this.onOpenResponseModal();
            return;
        }

        const isEnteringEditMode = !this.isInEditMode();
        this.isInEditMode.set(isEnteringEditMode);
        if (!isEnteringEditMode) return;

        const submission = this.activeSubmission();
        if (!submission) return;

        if (this.activeTab() === "submission") {
            this.editCleanedTextControl.reset(submission.cleanedText);
            this.removedEditTagKeys.set(new Set());
        } else if (this.activeTab() === "notes") {
            this.editNotesControl.reset(submission.notes ?? "");
        }
    }

    onOpenResponseModal(): void {
        const submission = this.activeSubmission();
        if (!submission) return;

        this.dialog
            .open<ResponseModalComponent, ModalData, ResponseModalResult>(ResponseModalComponent, {
                data: {
                    submission,
                    submitter: this.submitterFor(submission),
                    badges: this.badges() ?? [],
                    isEditing: this.hasResponse(submission),
                    // Lets the modal skip re-fetching audio we've already got cached.
                    audioPreviewUrl: this.activeResponseAudioUrl()
                },
                maxWidth: "80vw"
            })
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                if (result.audioChanged) {
                    const cachedUrl = this.responseAudioUrlCache.get(submission.id);
                    if (cachedUrl) URL.revokeObjectURL(cachedUrl);
                    this.responseAudioUrlCache.delete(submission.id);
                }

                this.dataSource.refreshPage();
            });
    }

    removeEditTag(tag: TagOption): void {
        this.removedEditTagKeys.update(keys => new Set(keys).add(tag.value as TagCategoryKey));
    }

    onUpdateSubmissionClick(): void {
        const cleanedText = this.editCleanedTextControl.value;
        const hasCleanedTextChanged = cleanedText !== this.activeSubmission()?.cleanedText;

        const request: SubmissionFreeformUpdateCleanedTextAndTagsRequest = {
            ...(hasCleanedTextChanged && { cleanedText }),
            ...Object.fromEntries(Array.from(this.removedEditTagKeys(), key => [key, null]))
        };

        if (Object.keys(request).length === 0) {
            this.toggleEditMode();
            return;
        }

        this.updateSubmission(
            submission => this.submissionFreeformApi.updateCleanedTextAndTags(submission.id, request),
            "We couldn't update the submission.",
            !!this.removedEditTagKeys().size
        );
    }

    onUpdateNotesClick(): void {
        this.updateSubmission(
            submission => this.submissionFreeformApi.updateNotes(submission.id, this.editNotesControl.value),
            "We couldn't update the submission notes."
        );
    }

    onSegmentChange(event: MatSelectChange): void {
        const previousSegmentId = this.activeSubmission()?.segmentId ?? null;
        this.updateSubmission(
            submission =>
                this.submissionFreeformApi
                    .updateSegment(submission.id, event.value)
                    .pipe(tap(() => this.segmentsRefetchRequested$.next())),
            "We couldn't update the segment.",
            false,
            // The [value] binding won't re-push an unchanged submission.segmentId, so revert the select by hand.
            () => (event.source.value = previousSegmentId)
        );
    }

    private updateSubmission(
        update: (submission: SubmissionFreeformResponse) => Observable<SubmissionFreeformResponse>,
        fallbackErrorMessage: string,
        refetchTags: boolean = false,
        onError?: () => void
    ): void {
        const submission = this.activeSubmission();
        if (!submission) return;
        update(submission)
            .pipe(
                tap(() => {
                    // the tags pipeline refreshes the submissions page once the refetch has completed
                    if (refetchTags) {
                        this.tagsRefetchRequested$.next();
                    } else {
                        this.dataSource.refreshPage();
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this.snackbar.show(error.error?.detail ?? fallbackErrorMessage, "error");
                    onError?.();
                    return of(null);
                })
            )
            .subscribe();
    }

    submissionTags(submission: SubmissionFreeformResponse): string[] {
        return TAG_CATEGORY_KEYS.map(key => submission[key]).filter((tag): tag is string => !!tag);
    }

    hasResponse(submission: SubmissionFreeformResponse): boolean {
        return !!(
            submission.responseRating ||
            submission.responseCustomText !== null ||
            submission.responseAudioPath ||
            submission.responseLink
        );
    }

    getResponseBadge(submission: SubmissionFreeformResponse): BadgeResponse | undefined {
        const badges = this.badges();
        const badge = badges?.find(b => b.id === submission.responseRating);
        if (submission.responseRating && !badge) {
            console.error(`Badge ${submission.responseRating} not found for submission ${submission.id}`);
        }
        return badge;
    }

    responseText(submission: SubmissionFreeformResponse): string | null {
        return submission.responseCustomText ?? this.getResponseBadge(submission)?.response ?? null;
    }

    isTagsOverlayOpen = false;

    showTagsOverlay(): void {
        this.isTagsOverlayOpen = true;
    }

    hideTagsOverlay(): void {
        this.isTagsOverlayOpen = false;
    }

    isActiveFiltersOverlayOpen = false;

    showActiveFiltersOverlay(): void {
        this.isActiveFiltersOverlayOpen = true;
    }

    hideActiveFiltersOverlay(): void {
        this.isActiveFiltersOverlayOpen = false;
    }

    isStatusLegendOpen = false;

    showStatusLegend(): void {
        this.isStatusLegendOpen = true;
    }

    hideStatusLegend(): void {
        this.isStatusLegendOpen = false;
    }

    protected readonly statusLegendPositions: ConnectedPosition[] = [
        { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetX: -24, offsetY: 4 },
        { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetX: -24, offsetY: -4 }
    ];

    protected readonly statusLegend = [
        { icon: "icon-iec-star-solid", label: "Favorited" },
        { icon: "icon-iec-paperclip", label: "Response Attached" },
        { icon: "icon-iec-view", label: "Public Display Allowed" },
        { icon: "icon-iec-megaphone", label: "Featured Submission" }
    ];

    protected readonly overlayPositions: ConnectedPosition[] = [
        {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 4
        },
        {
            originX: "center",
            originY: "top",
            overlayX: "center",
            overlayY: "bottom",
            offsetY: -4
        }
    ];
    protected readonly pageSizeOptions = [20, 50, 100];
    protected readonly filterByOptions = FILTER_BY_OPTIONS;
    protected readonly minScoreOptions = MIN_SCORE_OPTIONS;
    protected readonly publicDisplayOptions = PUBLIC_DISPLAY_OPTIONS;
    protected readonly visibilityOptions = VISIBILITY_OPTIONS;
    protected readonly queueOptions = Array.from({ length: MAX_QUEUED }, (_, i) => i + 1);
    protected readonly LabelPosition = LabelPosition;
    protected readonly ButtonVariant = ButtonVariant;
    protected readonly submissionTabs = computed(() =>
        this.isMarketer() ? SUBMISSION_TABS.filter(tab => tab !== "notes") : SUBMISSION_TABS
    );
    protected readonly maxVisibleSubmissionTags = MAX_VISIBLE_SUBMISSION_TAGS;
    protected readonly notesMaxLength = NOTES_MAX_LENGTH;
    protected readonly replaceAudioAcceptAttr = [
        ...new Set(
            Object.entries(AUDIO_FILE_EXTENSIONS).flatMap(([mimeType, extension]) => [mimeType, `.${extension}`])
        )
    ].join(",");
}
